import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styles from './ChatPage.module.css';
import { ChatWindow } from '../components/library/chat-interface/ChatWindow';
import { ChatInput } from '../components/library/chat-interface/ChatInput';
import { ChatStarters } from '../components/library/chat-interface/ChatStarters';
import { SessionUpdateBanner } from '../components/library/banners/SessionUpdateBanner';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useGlobalSession } from '../contexts/GlobalSessionContext';
import { v4 as uuidv4 } from 'uuid';
import logger from '../logging/logger';
import { buildManualOrderPrefillFromDraft } from '../features/order-ticket/manual/manualOrderDraftPrefill';
import { MessageFeedback, SessionService } from '../services/SessionService';

// Internal message structure (matches SessionChatPage)
interface InternalMessage {
  id: string;
  persistedMessageId?: string | null;
  role: 'user' | 'assistant';
  content: string;  // text content
  contentObj?: any; // structured content (charts, etc.)
  timestamp: string;
  feedback?: MessageFeedback | null;
  queuedFeedback?: MessageFeedback | null;
  hasQueuedFeedback?: boolean;
  feedbackSyncState?: 'synced' | 'pending' | 'failed';
}

// Backtest progress event data
interface BacktestProgressEvent {
  progressId: string;  // Fixed ID for the progress message
  progress: number;
  phase: 'planning' | 'loading_data' | 'executing' | 'loading_positions' | 'complete' | 'failed';
  currentPeriod?: string;
  currentSymbol?: string;
  error?: string;
  results?: unknown;  // Backtest results for chart rendering (legacy or enriched payload)
}

interface NewChatPageProps {
  sidebarCollapsed: boolean;
  onScrollDirectionChange?: (isScrollingDown: boolean) => void;
}

// Helpers to normalize websocket payloads (same as SessionChatPage)
const normalizeWsContent = (payload: any): { text: string; obj?: any } => {
  const c = payload?.content;
  if (typeof payload?.delta === 'string') return { text: payload.delta };
  if (typeof c === 'string') return { text: c };
  if (c && typeof c === 'object') return { text: c.text ?? '', obj: c };
  if (typeof payload?.text === 'string') return { text: payload.text };
  return { text: '' };
};

const normalizeChartPayload = (payload: any) => {
  if (payload?.data !== undefined) return payload.data;
  if (payload?.chart !== undefined) return payload.chart;
  return payload;
};

export const NewChatPage: React.FC<NewChatPageProps> = ({ sidebarCollapsed, onScrollDirectionChange }) => {
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [isResponding, setIsResponding] = useState(false);
  const [showStarters, setShowStarters] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isWebSocketReady, setIsWebSocketReady] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isSessionBusy, setIsSessionBusy] = useState(false);
  const [hasSessionUpdates, setHasSessionUpdates] = useState(false);
  const [agentActivity, setAgentActivity] = useState<string | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const currentStreamIdRef = useRef<string | null>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);
  const hasSentPendingMessageRef = useRef(false);
  const hasTitleGeneratedRef = useRef(false);
  const hasNavigatedRef = useRef(false);
  const isTitleGenerationInProgressRef = useRef(false);
  const messagesRef = useRef<InternalMessage[]>([]);
  const feedbackSaveInFlightRef = useRef<Set<string>>(new Set());

  const { user, userUuid, isAuthenticated, getToken } = useAuth();
  const { sendMessage, stopGeneration, subscribeToSession } = useWebSocket();
  const { createSession, generateTitleFromMessage, generateTitleFromHistory, updateSessionTitleLocally, selectSession, currentSession, clearCurrentSession, sessions } = useGlobalSession();
  const sessionService = useMemo(() => new SessionService(), []);

  // On entering a fresh New Chat, drop any previously-active session so a backtest
  // run here binds to this chat's own session (created on first message), never a
  // stale one. The chat's real session is set later via createSession/selectSession.
  useEffect(() => {
    clearCurrentSession();
  }, [clearCurrentSession]);

  // Determine if user is first-time (no existing sessions)
  const isFirstTimeUser = sessions.length === 0;

  // Scroll to top when component mounts or when showing starters (mobile only)
  useEffect(() => {
    if (showStarters && messages.length === 0 && chatContainerRef.current) {
      // Only scroll to top on mobile (viewport width <= 768px)
      if (window.innerWidth <= 768) {
        chatContainerRef.current.scrollTop = 0;
      }
    }
  }, [showStarters, messages.length]);

  // Format messages for ChatWindow (same pattern as SessionChatPage)
  const formattedMessages = useMemo(() => {
    return messages.map((msg) => ({
      id: msg.id,
      user: msg.role === 'user' ? 'user' : 'AI',
      text: msg.content,
      feedback: msg.feedback ?? null,
      content: msg.contentObj, // ChatWindow reads chart data from here
      timestamp: msg.timestamp,
    }));
  }, [messages]);
  const createMessageObj = useCallback((content: string, role: 'user' | 'assistant'): InternalMessage => ({
    id: uuidv4(),
    persistedMessageId: null,
    role,
    content,
    contentObj: undefined,
    timestamp: new Date().toISOString(),
    feedback: null,
    hasQueuedFeedback: false,
    feedbackSyncState: 'synced',
  }), []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const persistQueuedFeedback = useCallback(async (
    localMessageId: string,
    persistedMessageId: string,
    feedbackToPersist: MessageFeedback | null
  ): Promise<void> => {
    if (!currentSessionId || !userUuid) return;
    if (feedbackSaveInFlightRef.current.has(localMessageId)) return;

    feedbackSaveInFlightRef.current.add(localMessageId);
    try {
      const token = await getToken();
      if (!token) {
        logger.warn('Feedback save deferred: missing auth token', { localMessageId, persistedMessageId });
        return;
      }

      await sessionService.updateMessageFeedback(
        currentSessionId,
        persistedMessageId,
        userUuid,
        token,
        feedbackToPersist
      );

      const latest = messagesRef.current.find((m) => m.id === localMessageId);
      if (!latest) return;
      if (latest.hasQueuedFeedback && latest.queuedFeedback !== feedbackToPersist) {
        return;
      }

      setMessages((prev) => prev.map((msg) =>
        msg.id === localMessageId
          ? {
              ...msg,
              hasQueuedFeedback: false,
              queuedFeedback: undefined,
              feedbackSyncState: 'synced',
            }
          : msg
      ));
      logger.info('Feedback persisted successfully', { localMessageId, persistedMessageId, feedbackToPersist });
    } catch (error: any) {
      logger.error('Failed to persist queued feedback', {
        localMessageId,
        persistedMessageId,
        feedbackToPersist,
        status: error?.status ?? null,
        error: error?.message || String(error),
      });
      setMessages((prev) => prev.map((msg) =>
        msg.id === localMessageId
          ? {
              ...msg,
              hasQueuedFeedback: true,
              queuedFeedback: feedbackToPersist,
              feedbackSyncState: 'failed',
            }
          : msg
      ));
    } finally {
      feedbackSaveInFlightRef.current.delete(localMessageId);
      const latest = messagesRef.current.find((m) => m.id === localMessageId);
      if (
        latest?.persistedMessageId &&
        latest?.hasQueuedFeedback &&
        latest?.feedbackSyncState === 'pending' &&
        latest?.queuedFeedback !== feedbackToPersist &&
        !feedbackSaveInFlightRef.current.has(localMessageId)
      ) {
        void persistQueuedFeedback(
          localMessageId,
          latest.persistedMessageId,
          latest.queuedFeedback ?? null
        );
      }
    }
  }, [currentSessionId, userUuid, getToken, sessionService]);

  const handleMessageFeedbackChange = useCallback(async (
    messageId: string,
    feedback: MessageFeedback | null
  ): Promise<boolean> => {
    const targetMessage = messagesRef.current.find((msg) => msg.id === messageId);
    if (targetMessage && targetMessage.role !== 'assistant') {
      return false;
    }

    setMessages((prev) => prev.map((msg) =>
      msg.id === messageId
        ? {
            ...msg,
            feedback,
            hasQueuedFeedback: true,
            queuedFeedback: feedback,
            feedbackSyncState: 'pending',
          }
        : msg
    ));

    const persistedMessageId = targetMessage?.persistedMessageId;
    if (!persistedMessageId) {
      logger.info('Feedback queued until message persistence completes', {
        localMessageId: messageId,
        persistedMessageId: null,
        feedback,
      });
      return true;
    }

    void persistQueuedFeedback(messageId, persistedMessageId, feedback);
    return true;
  }, [persistQueuedFeedback]);

  useEffect(() => {
    if (!userUuid) return;
    messagesRef.current.forEach((message) => {
      if (
        message.role === 'assistant' &&
        message.persistedMessageId &&
        message.hasQueuedFeedback &&
        message.feedbackSyncState === 'pending' &&
        !feedbackSaveInFlightRef.current.has(message.id)
      ) {
        void persistQueuedFeedback(
          message.id,
          message.persistedMessageId,
          message.queuedFeedback ?? null
        );
      }
    });
  }, [userUuid, messages, persistQueuedFeedback]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    // Use the ChatWindow's internal scroll function if available
    if ((window as any).__chatWindowScrollToBottom) {
      (window as any).__chatWindowScrollToBottom();
    } else if (chatContentRef.current) {
      // Fallback to scrolling the content container
      chatContentRef.current.scrollTo({
        top: chatContentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll direction tracking for mobile header hide/show
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent || !onScrollDirectionChange) return;

    const handleScroll = () => {
      const currentScrollTop = chatContent.scrollTop;
      const scrollThreshold = 10; // Minimum scroll distance to trigger
      
      // Only trigger if scrolled more than threshold
      if (Math.abs(currentScrollTop - lastScrollTopRef.current) > scrollThreshold) {
        const isScrollingDown = currentScrollTop > lastScrollTopRef.current;
        
        // Show header when at top or scrolling up
        if (currentScrollTop <= 10) {
          onScrollDirectionChange(false);
        } else {
          onScrollDirectionChange(isScrollingDown);
        }
        
        lastScrollTopRef.current = currentScrollTop;
      }
    };

    chatContent.addEventListener('scroll', handleScroll, { passive: true });
    return () => chatContent.removeEventListener('scroll', handleScroll);
  }, [onScrollDirectionChange]);

  // Dispatch streaming state changes to lock/unlock SideConfigMenu
  // Lock when agent is working (waiting for response OR actively streaming)
  useEffect(() => {
    const isAgentWorking = isWaitingForResponse || isResponding;
    window.dispatchEvent(new CustomEvent('streamingStateChange', {
      detail: { isStreaming: isAgentWorking }
    }));
  }, [isWaitingForResponse, isResponding]);

  // Listen for backtest progress events - single message that updates in place
  useEffect(() => {
    // Handle progress updates (create or update the progress message atomically)
    const handleBacktestProgress = (event: CustomEvent<BacktestProgressEvent>) => {
      const { progressId, progress, phase, currentPeriod, currentSymbol, error, results } = event.detail;
      logger.info('📊 [NewChatPage] Backtest progress:', { progressId, progress, phase, currentPeriod, currentSymbol, hasResults: !!results });

      setMessages(prev => {
        const existingIndex = prev.findIndex(m => m.id === progressId);

        const progressMessage: InternalMessage = {
          id: progressId,
          role: 'assistant',
          content: '', // No text content - progress bar only
          contentObj: {
            backtest_progress: {
              progress,
              phase,
              currentPeriod,
              currentSymbol,
              isComplete: phase === 'complete',
              error,
            },
            // Include backtest results when available (on complete)
            ...(results !== undefined ? { backtest_results: results } : {}),
          },
          timestamp: new Date().toISOString(),
        };

        if (existingIndex >= 0) {
          // Update existing message in place
          const updated = [...prev];
          updated[existingIndex] = progressMessage;
          return updated;
        } else {
          // Add new progress message
          return [...prev, progressMessage];
        }
      });
    };

    // Handle backtest result saved - adds backtest_result reference for persistence
    const handleBacktestResultSaved = (event: CustomEvent<{
      progressId: string;
      backtestConfigId: string;
      resultsDataId: string;
    }>) => {
      const { progressId, backtestConfigId, resultsDataId } = event.detail;
      logger.info('💾 [NewChatPage] Backtest result saved:', { progressId, backtestConfigId, resultsDataId });

      // Update the existing progress message with backtest_result reference
      // and add to new_data_ids for reuse via data_id system
      setMessages(prev => prev.map(msg => {
        if (msg.id !== progressId) return msg;
        return {
          ...msg,
          contentObj: {
            ...(msg.contentObj ?? {}),
            backtest_result: {
              backtest_config_id: backtestConfigId,
              results_data_id: resultsDataId,
            },
            new_data_ids: {
              ...((msg.contentObj as any)?.new_data_ids ?? {}),
              [resultsDataId]: `Backtest results for config ${backtestConfigId}`,
            },
          },
        };
      }));
    };

    const handleBacktestAssistantMessage = (event: CustomEvent<{
      progressId?: string;
      messageId: string;
      text: string;
    }>) => {
      const { progressId, messageId, text } = event.detail;
      if (!messageId || !text) return;

      logger.info('💬 [NewChatPage] Backtest assistant message:', { progressId, messageId });
      setMessages(prev => {
        const withoutProgress = progressId
          ? prev.filter(m => m.id !== progressId)
          : prev;
        const existingIndex = withoutProgress.findIndex(m => m.id === messageId);
        const assistantMessage: InternalMessage = {
          id: messageId,
          role: 'assistant',
          content: text,
          timestamp: new Date().toISOString(),
        };

        if (existingIndex >= 0) {
          const updated = [...withoutProgress];
          updated[existingIndex] = assistantMessage;
          return updated;
        }
        return [...withoutProgress, assistantMessage];
      });
    };

    // Remove an in-progress backtest bar when the run is aborted before it starts
    // (e.g. a name conflict that needs the user to rename or overwrite).
    const handleBacktestProgressRemove = (event: CustomEvent<{ progressId: string }>) => {
      const { progressId } = event.detail;
      if (!progressId) return;
      logger.info('🧹 [NewChatPage] Removing backtest progress bar:', progressId);
      setMessages(prev => prev.filter(m => m.id !== progressId));
    };

    window.addEventListener('backtestProgress', handleBacktestProgress as EventListener);
    window.addEventListener('backtestResultSaved', handleBacktestResultSaved as EventListener);
    window.addEventListener(
      'backtestAssistantMessage',
      handleBacktestAssistantMessage as EventListener
    );
    window.addEventListener('backtestProgressRemove', handleBacktestProgressRemove as EventListener);
    return () => {
      window.removeEventListener('backtestProgress', handleBacktestProgress as EventListener);
      window.removeEventListener('backtestResultSaved', handleBacktestResultSaved as EventListener);
      window.removeEventListener(
        'backtestAssistantMessage',
        handleBacktestAssistantMessage as EventListener
      );
      window.removeEventListener('backtestProgressRemove', handleBacktestProgressRemove as EventListener);
    };
  }, []);

  // Subscribe to WebSocket when session is created (draft mode)
  useEffect(() => {
    if (!currentSessionId) return;

    logger.info('🔌 [NewChatPage] Subscribing to session for real-time streaming:', currentSessionId);

    const unsubscribe = subscribeToSession(currentSessionId, {
      onSessionReady: (data: any) => {
        logger.info('✅ [NewChatPage] Session ready:', data);
        setIsWebSocketReady(true);
        setIsSessionBusy(false); // Session is ready, not busy
      },

      onSessionBusy: (data: any) => {
        logger.info('🚫 [NewChatPage] Session busy:', data);
        setIsSessionBusy(true);
      },

      onSessionUpdated: (data: any) => {
        logger.info('🔄 [NewChatPage] Session updated:', data);
        setHasSessionUpdates(true);
      },

      onAgentActivity: (data: any) => {
        // Update activity text (ephemeral, not persisted)
        const activity = data?.activity || null;
        setAgentActivity(activity);
      },

      onMessageStart: (data: any) => {
        logger.info('🌊 [NewChatPage] Message started:', data);
        setIsWaitingForResponse(false); // AI started responding
        // Clear activity when AI starts responding
        setAgentActivity(null);
        const streamSourceId = data.message_id || uuidv4();
        const localMessageId = `assistant_stream_${streamSourceId}`;
        currentStreamIdRef.current = localMessageId;

        const newMessage = createMessageObj('', 'assistant');
        newMessage.id = localMessageId;
        newMessage.persistedMessageId = null;
        newMessage.hasQueuedFeedback = false;
        newMessage.feedbackSyncState = 'synced';

        setMessages(prev => [...prev, newMessage]);
        setIsResponding(true);
        setIsSessionBusy(false); // We're the active client, not busy for us
      },

      onContentDelta: (data: any) => {
        const { text } = normalizeWsContent(data);
        if (currentStreamIdRef.current && text) {
          setMessages(prev => prev.map(msg =>
            msg.id === currentStreamIdRef.current
              ? { ...msg, content: msg.content + text }
              : msg
          ));
        }
      },

      onChartData: (data: any) => {
        logger.info('📊 [NewChatPage] Chart data:', data);
        const chart = normalizeChartPayload(data);

        if (!currentStreamIdRef.current) return;

        // Set as embedded_chart in contentObj (same as SessionChatPage)
        setMessages(prev => prev.map(msg => {
          if (msg.id !== currentStreamIdRef.current) return msg;

          // Merge contentObj to preserve existing data
          const nextContentObj = {
            ...(msg.contentObj ?? {}),
            embedded_chart: chart,
          };

          return { ...msg, contentObj: nextContentObj };
        }));
      },

      onBacktestConfig: async (data: any) => {
        logger.info('📋 [NewChatPage] Backtest config received:', data);
        const backtestConfigId = data?.backtest_config_id;
        const sourceTriggerMessageId = data?.source_trigger_message_id;
        if (!backtestConfigId) {
          logger.warn('[NewChatPage] No backtest_config_id in data');
          return;
        }

        try {
          // Import BacktestService dynamically to avoid circular deps
          const { backtestService } = await import('../features/order-ticket/strategy-backtest/services/BacktestService');

          const token = await getToken();
          if (!token || !user?.email) {
            logger.error('[NewChatPage] Cannot fetch backtest config: missing auth');
            return;
          }

          const config = await backtestService.getBacktestConfig(
            currentSessionId,
            backtestConfigId,
            user.email,
            token
          );

          if (config) {
            logger.info('='.repeat(60));
            logger.info('📋 [NewChatPage] AI-Generated Backtest Config:');
            logger.info(JSON.stringify(config, null, 2));
            logger.info('='.repeat(60));

            // Open the side config menu with backtest tab selected
            window.dispatchEvent(new CustomEvent('openSideConfigMenu', { detail: { tab: 'backtest' } }));

            // Give the panel time to open and mount BacktestConfig, then apply config
            // Pass both config and configId so results can be saved later
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('applyBacktestConfig', {
                detail: {
                  config,
                  configId: backtestConfigId,
                  sourceTriggerMessageId,
                }
              }));
              logger.info('✅ [NewChatPage] Backtest config dispatched to UI with configId:', backtestConfigId);
            }, 100);
          }
        } catch (error) {
          logger.error('[NewChatPage] Failed to fetch/apply backtest config:', error);
        }
      },

      onManualOrderDraft: (data: any) => {
        logger.info('🧾 [NewChatPage] Manual order draft received:', data);
        const prefill = buildManualOrderPrefillFromDraft(data);
        if (!prefill) {
          logger.warn('[NewChatPage] Invalid manual_order_draft payload, ignoring event', data);
          return;
        }

        window.dispatchEvent(
          new CustomEvent('openManualOrderTicket', {
            detail: { prefill },
          }),
        );
      },

      onMessageComplete: (data: any) => {
        logger.info('🏁 [NewChatPage] Message complete - raw data:', data);
        logger.info('🏁 [NewChatPage] Message complete - data.content:', data?.content);
        logger.info('🏁 [NewChatPage] currentStreamIdRef:', currentStreamIdRef.current);
        setIsWaitingForResponse(false);

        if (currentStreamIdRef.current) {
          const { text, obj } = normalizeWsContent(data);
          const finalMessageId = data?.message_id || currentStreamIdRef.current;
          logger.info('🏁 [NewChatPage] Normalized - text length:', text?.length, 'obj:', obj);

          // Chart data is in data.content.embedded_chart
          const embeddedChart = obj?.embedded_chart || data?.content?.embedded_chart;
          logger.info('🏁 [NewChatPage] embedded_chart:', embeddedChart);

          // Update the CURRENT streaming message (use currentStreamIdRef, not data.message_id)
          const streamingMsgId = currentStreamIdRef.current;
          const targetMessage = messagesRef.current.find((m) => m.id === streamingMsgId);
          const queuedFeedback = targetMessage?.hasQueuedFeedback
            ? (targetMessage?.queuedFeedback ?? null)
            : undefined;

          setMessages(prev => {
            logger.info('🏁 [NewChatPage] Current messages:', prev.map(m => ({ id: m.id, role: m.role })));
            
            return prev.map(msg => {
              logger.info('🏁 [NewChatPage] Checking msg:', msg.id, 'vs streamingMsgId:', streamingMsgId, 'match:', msg.id === streamingMsgId);
              
              if (msg.id !== streamingMsgId) return msg;
              
              // Merge contentObj - preserve existing + add new
              const nextContentObj = {
                ...(msg.contentObj ?? {}),
                ...(obj ?? {}),
              };
              
              // If embedded_chart found, ensure it's in contentObj
              if (embeddedChart) {
                nextContentObj.embedded_chart = embeddedChart;
              }
              
              logger.info('🏁 [NewChatPage] ✅ Updating message:', msg.id, 'with contentObj:', nextContentObj);
              
              return { 
                ...msg,
                persistedMessageId: finalMessageId,
                content: text || msg.content,
                contentObj: Object.keys(nextContentObj).length > 0 ? nextContentObj : msg.contentObj,
              };
            });
          });

          if (queuedFeedback !== undefined) {
            setMessages((prev) => prev.map((msg) =>
              msg.id === streamingMsgId
                ? { ...msg, feedbackSyncState: 'pending' }
                : msg
            ));
            void persistQueuedFeedback(streamingMsgId, finalMessageId, queuedFeedback);
          }

          currentStreamIdRef.current = null;
        }

        // Ensure lock state is released even if message lifecycle events are partial.
        setIsResponding(false);
      },

      onGenerationStopped: (data: any) => {
        logger.info('⏹️ [NewChatPage] Generation stopped:', data);
        setIsWaitingForResponse(false);
        if (currentStreamIdRef.current) {
          currentStreamIdRef.current = null;
        }
        setIsResponding(false);
        setIsSessionBusy(false);
      },

      onError: (data: any) => {
        logger.error('❌ [NewChatPage] WebSocket error:', data);
        setIsWaitingForResponse(false);
        setIsResponding(false);

        // Don't show error message for disconnect events - just reset UI state
        if (data?.is_disconnect) {
          logger.info('🔌 [NewChatPage] WebSocket disconnected, resetting UI state');
          currentStreamIdRef.current = null;
          return;
        }

        const errText = data?.message || 'Unknown error';

        if (currentStreamIdRef.current) {
          setMessages(prev => prev.map(msg =>
            msg.id === currentStreamIdRef.current
              ? { ...msg, content: `Error: ${errText}` }
              : msg
          ));
          currentStreamIdRef.current = null;
        } else {
          setMessages(prev => [...prev, createMessageObj(`Error: ${errText}`, 'assistant')]);
        }
      },

      onRateLimitError: (data: any) => {
        logger.warn('⚠️ [NewChatPage] Rate limit exceeded:', data);
        setIsWaitingForResponse(false);
        setIsResponding(false);

        // Calculate human-readable retry time
        const retryAfterSeconds = data?.details?.retry_after_seconds || 60;
        const retryTime = new Date(Date.now() + retryAfterSeconds * 1000);
        const timeString = retryTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        
        // Determine which limit was hit
        let limitMessage = 'Rate limit exceeded.';
        if (data?.details?.limits) {
          const limits = data.details.limits;
          const usage = data.details.current_usage || {};
          
          if (retryAfterSeconds <= 60) {
            limitMessage = `You've reached your per-minute limit (${usage.minute || 0}/${limits.tokens_per_minute} tokens).`;
          } else if (retryAfterSeconds <= 3600) {
            limitMessage = `You've reached your hourly limit (${usage.hour || 0}/${limits.tokens_per_hour} tokens).`;
          } else if (retryAfterSeconds <= 86400) {
            limitMessage = `You've reached your daily limit (${usage.day || 0}/${limits.tokens_per_day} tokens).`;
          } else {
            limitMessage = `You've reached your monthly limit.`;
          }
        }

        const errorMessage = `${limitMessage} Please try again after ${timeString}.`;
        
        if (currentStreamIdRef.current) {
          setMessages(prev => prev.map(msg =>
            msg.id === currentStreamIdRef.current
              ? { ...msg, content: errorMessage }
              : msg
          ));
          currentStreamIdRef.current = null;
        } else {
          setMessages(prev => [...prev, createMessageObj(errorMessage, 'assistant')]);
        }
      },
    });

    return () => {
      logger.info('🔌 [NewChatPage] Unsubscribing from session:', currentSessionId);
      setIsWebSocketReady(false);
      currentStreamIdRef.current = null;
      unsubscribe();
    };
  }, [currentSessionId, subscribeToSession, createMessageObj, getToken, user?.email]);

  // Send pending message once WebSocket is ready, update URL silently (no navigation)
  useEffect(() => {
    if (isWebSocketReady && pendingMessage && currentSessionId && !hasSentPendingMessageRef.current) {
      logger.info('🚀 [NewChatPage] WebSocket ready, sending pending message:', pendingMessage);
      hasSentPendingMessageRef.current = true;
      
      // Send the message
      sendMessage(currentSessionId, pendingMessage);
      setPendingMessage(null);
      
      // Just update URL silently - no navigation, no page reload
      // User stays on NewChatPage which handles the streaming
      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        selectSession(currentSessionId);
        window.history.replaceState(null, '', `/${currentSessionId}`);
        logger.info('🔗 [NewChatPage] URL updated silently to:', `/${currentSessionId}`);
      }
    }
  }, [isWebSocketReady, pendingMessage, currentSessionId, sendMessage, selectSession]);

  const handleSendMessage = async (message: string) => {
    logger.info('[NewChatPage] Sending message:', message);

    if (!isAuthenticated || !user) {
      logger.error('❌ User not authenticated');
      return;
    }

    // If session already exists, send message to existing session instead of creating new one
    if (currentSessionId) {
      logger.info('📤 [NewChatPage] Session exists, sending to existing session:', currentSessionId);
      logger.info('📤 [NewChatPage] Setting isWaitingForResponse to true');
      const userMessage = createMessageObj(message, 'user');
      setMessages(prev => [...prev, userMessage]);
      setIsWaitingForResponse(true); // Show typing dots while waiting for AI
      logger.info('📤 [NewChatPage] Calling sendMessage via WebSocket');
      sendMessage(currentSessionId, message);

      // Check if title is still "New Chat" (greeting was detected) and generate from history
      // This handles the case where user started with a greeting and now sends a substantive message
      const currentTitle = currentSession?.title;
      if (
        currentTitle === 'New Chat' &&
        !isTitleGenerationInProgressRef.current
      ) {
        isTitleGenerationInProgressRef.current = true;
        logger.info('📝 [NewChatPage] Title is still "New Chat", generating from history...');

        generateTitleFromHistory(currentSessionId, (streamingTitle: string) => {
          logger.info('📝 [NewChatPage] Title streaming:', streamingTitle);
          updateSessionTitleLocally(currentSessionId, streamingTitle);
        }).then(({ title }) => {
          logger.info('✅ [NewChatPage] Title generation from history complete:', title);
          updateSessionTitleLocally(currentSessionId, title);
          isTitleGenerationInProgressRef.current = false;
        }).catch((error: Error) => {
          logger.error('❌ [NewChatPage] Failed to generate title from history:', error);
          isTitleGenerationInProgressRef.current = false;
        });
      }
      return;
    }

    // Step 1: Immediately show user message AND typing dots
    setShowStarters(false);
    const userMessage = createMessageObj(message, 'user');
    setMessages([userMessage]);
    setIsWaitingForResponse(true);

    // Step 2: Create session in background
    try {
      logger.info('📝 [NewChatPage] Creating session in background...');
      setIsCreatingSession(true);

      const session = await createSession();
      const sessionId = session.id;

      logger.info('✅ [NewChatPage] Session created:', sessionId);

      // Reset title generation flag for new session
      hasTitleGeneratedRef.current = false;

      // Step 3: Generate title from user message immediately (non-blocking)
      // This runs in parallel with WebSocket setup - doesn't block the chat flow
      if (!hasTitleGeneratedRef.current) {
        hasTitleGeneratedRef.current = true;
        generateTitleFromMessage(sessionId, message, (streamingTitle: string) => {
          logger.info('📝 [NewChatPage] Title streaming:', streamingTitle);
          updateSessionTitleLocally(sessionId, streamingTitle);
        }).then(({ title, skipped }) => {
          if (skipped) {
            logger.info('📝 [NewChatPage] Title generation skipped (greeting detected) - will be handled by SessionChatPage');
            // Reset the flag so SessionChatPage can try again with history
            hasTitleGeneratedRef.current = false;
          } else {
            logger.info('✅ [NewChatPage] Title generation complete:', title);
            updateSessionTitleLocally(sessionId, title);
          }
        }).catch((error: Error) => {
          logger.error('❌ [NewChatPage] Failed to generate title:', error);
          // Reset flag on error so fallback can work
          hasTitleGeneratedRef.current = false;
        });
      }

      // Step 4: Set session ID (triggers WebSocket subscription)
      setCurrentSessionId(sessionId);
      setPendingMessage(message);
      hasSentPendingMessageRef.current = false;
      setIsCreatingSession(false);

      // Note: URL will be updated once WebSocket is ready (in useEffect above)

    } catch (error) {
      logger.error('❌ [NewChatPage] Failed to create session:', error);
      setIsCreatingSession(false);
      setMessages(prev => [
        ...prev,
        createMessageObj('Sorry, I encountered an error creating the session. Please try again.', 'assistant')
      ]);
    }
  };

  const handleStopMessage = () => {
    if (currentSessionId) {
      logger.info('🛑 [NewChatPage] Stopping generation for session:', currentSessionId);
      stopGeneration(currentSessionId);
      setIsResponding(false);
      setIsWaitingForResponse(false); // Reset waiting state when stopping
    }
  };

  const handleRefreshSession = useCallback(() => {
    logger.info('🔄 [NewChatPage] Refreshing session');
    setHasSessionUpdates(false);
    
    // Reload the page to get fresh data
    if (currentSessionId) {
      window.location.href = `/${currentSessionId}`;
    }
  }, [currentSessionId]);

  const handleDismissUpdate = useCallback(() => {
    logger.info('❌ [NewChatPage] Dismissing session update notification');
    setHasSessionUpdates(false);
  }, []);

  return (
    <div 
      ref={chatContainerRef}
      className={`${styles.chatContainer} ${sidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded} ${isCreatingSession ? styles.creating : ''} ${showStarters && messages.length === 0 ? styles.startersLayout : ''}`}
    >
      <SessionUpdateBanner
        isVisible={hasSessionUpdates}
        onRefresh={handleRefreshSession}
        onDismiss={handleDismissUpdate}
      />
      {showStarters && messages.length === 0 ? (
        <>
          <div className={styles.startersWrapper}>
            <ChatStarters onStarterClick={handleSendMessage} isFirstTimeUser={isFirstTimeUser} />
          </div>
          <div className={styles.chatInputContainer}>
            <ChatInput
              onSend={handleSendMessage}
              onStop={handleStopMessage}
              isBottom={true}
              isProcessing={isResponding || isCreatingSession}
              isFirstMessage={true}
              isSessionBusy={isSessionBusy}
            />
          </div>
        </>
      ) : (
        <>
          <div className={styles.chatContent} ref={chatContentRef}>
            <ChatWindow
              messages={formattedMessages}
              isBottom={true}
              isLoading={isCreatingSession}
              isWaitingForResponse={isWaitingForResponse}
              isStreaming={isResponding}
              sessionId={currentSessionId || undefined}
              agentActivity={agentActivity}
              onMessageFeedbackChange={handleMessageFeedbackChange}
              onScrollButtonVisibilityChange={setShowScrollToBottom}
              onScrollToBottom={scrollToBottom}
            />
          </div>
          <div className={styles.chatInputContainer}>
            {showScrollToBottom && (
              <button
                className={styles.scrollToBottomButton}
                onClick={scrollToBottom}
                aria-label="Scroll to bottom"
              >
                <div className={styles.arrow}></div>
              </button>
            )}
            <ChatInput
              onSend={handleSendMessage}
              onStop={handleStopMessage}
              isBottom={true}
              isProcessing={isResponding || isCreatingSession || isWaitingForResponse}
              isFirstMessage={false}
              isSessionBusy={isSessionBusy}
            />
          </div>
        </>
      )}
    </div>
  );
};
